import { ChangeEvent, FC, useCallback } from 'react';

import { Button, Flex, HStack, rem, Select, Text } from '@bonusly/design-system';
import { useRecognizeURL } from '@bonusly/url';
import { useCurrentUser } from '@bonusly/who-am-i';

import { BugRecorder } from './bug-recorder';
import { useRecentSpoofUsers } from './use-recent-spoof-users';

export const SpoofingWarning: FC = () => {
	const currentUser = useCurrentUser();
	const stopSpoofingUrl = useRecognizeURL('/super_admin/spoof/stop_spoofing');
	const spoofCreateUrl = useRecognizeURL('/super_admin/spoof');
	const { recentUsers } = useRecentSpoofUsers();

	const handleUserChange = useCallback(
		(e: ChangeEvent<HTMLSelectElement>) => {
			const userId = e.target.value;
			if (userId) {
				window.location.href = `${spoofCreateUrl}?user_id=${userId}`;
			}
		},
		[spoofCreateUrl]
	);

	return (
		<Flex
			bgColor="yellow.50"
			data-testid="spoofing-warning"
			py={rem(18)}
			paddingInline={{ base: '0.75rem', xs: '1.5rem' }}
			margin="0 auto"
			alignItems="center"
			justifyContent="space-between"
			position="sticky"
			top={0}
			zIndex={1000}
		>
			<Flex alignItems="center">
				<Text display="inline" color="yellow.700" fontSize="md" fontWeight="normal" lineHeight={6} margin={0}>
					<Text as="span" fontWeight="semibold" fontSize="md">
						Warning!
					</Text>{' '}
					You are currently in spoofing mode and at risk for modifying customer data.
				</Text>
			</Flex>
			<HStack spacing={2}>
				{recentUsers.length > 0 && (
					<Select
						data-testid="recent-spoof-users"
						placeholder={currentUser?.email ?? 'Recent users...'}
						size="sm"
						bgColor="grayAlpha.100"
						border="none"
						color="gray.800"
						fontSize="md"
						maxW="220px"
						borderRadius={rem(8)}
						onChange={handleUserChange}
						_hover={{ bgColor: 'grayAlpha.100', cursor: 'pointer' }}
						_focus={{ border: 'none' }}
					>
						{recentUsers.map((user) => (
							<option key={user.id} value={user.id}>
								{user.email}
							</option>
						))}
					</Select>
				)}
				<BugRecorder variant="banner" />
				<Button
					as="a"
					href={stopSpoofingUrl}
					bgColor="grayAlpha.100"
					color="gray.800"
					size="sm"
					fontSize="md"
					px={4}
					py={1}
					borderRadius={rem(8)}
					overflow="hidden"
					_focus={{ border: 'none' }}
					_hover={{ bgColor: 'grayAlpha.100', cursor: 'pointer' }}
					_active={{ bgColor: 'grayAlpha.100' }}
				>
					Stop spoofing
				</Button>
			</HStack>
		</Flex>
	);
};
